import { router } from "@inertiajs/react"
import { AlertCircle } from "lucide-react"
import { CalendarCheck, PhoneForwarded, Plug } from "lucide-react"
import { type FormEvent, useState } from "react"

import calComIcon from "@/assets/cal_com.svg"
import calendlyIcon from "@/assets/calendly.svg"
import { CapabilityCard } from "@/components/agents/setup/capability-card"
import {
  formatPhone,
  isValidUsPhoneNumber,
} from "@/components/agents/setup/receptionist/phone-utils"
import type {
  CalComEventType,
  CalendarKind,
  CalendlyEventType,
  ConnectedCalendars,
} from "@/components/agents/setup/types"
import { WeeklyHoursGrid } from "@/components/business-profile/hours-editor"
import { FormField } from "@/components/form-field"
import {
  type PatCredentials,
  PatCredentialsForm,
} from "@/components/integrations/pat-credentials-form"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import {
  settingsIntegrationCredentialConnectPath,
  settingsMcpConnectPath,
} from "@/routes"

import type { LeadResponderSetupData, Persistence, SpeedTarget } from "./types"

// IMP-409: real calendar connection state + live event types for the Booking &
// handoff step / tab, supplied by the server (connected_calendars,
// calendly_event_types, cal_com_event_types). Replaces the old hardcoded mock.
// returnTo is where a connect flow (cal.com PAT dialog / Calendly OAuth) lands
// the user back — the current wizard step or the dashboard.
export interface CalendarConnect {
  connectedCalendars: ConnectedCalendars
  calendlyEventTypes: CalendlyEventType[]
  calComEventTypes: CalComEventType[]
  returnTo: string
}

// Shared field bodies for each step, so the setup wizard and the dashboard's
// edit drawer render exactly the same controls (one source of truth).
interface FieldsProps {
  data: LeadResponderSetupData
  onUpdate: (patch: Partial<LeadResponderSetupData>) => void
}

// Selectable option cards used for tone / speed / persistence.
function OptionCards<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string; sub: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {options.map((opt) => {
        const selected = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "rounded-lg border p-4 text-left transition-colors",
              selected
                ? "border-blue-300 bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/60 dark:to-blue-900/20"
                : "border-border hover:bg-muted/30",
            )}
          >
            <p
              className={cn(
                "text-sm leading-tight font-semibold",
                selected && "text-blue-600 dark:text-blue-400",
              )}
            >
              {opt.label}
            </p>
            <p
              className={cn(
                "mt-0.5 text-xs leading-relaxed",
                selected
                  ? "text-blue-600 dark:text-blue-400"
                  : "text-muted-foreground",
              )}
            >
              {opt.sub}
            </p>
          </button>
        )
      })}
    </div>
  )
}

// IMP-488: the Qualifying configuration (Criteria list + Additional
// instructions) is gone. Judging a lead is now derived from what the assistant
// knows about the business, so every agent judges the same person the same way
// — and an SMB owner was never well placed to author a criteria list anyway.
// Custom instructions remain the escape hatch for steering.

// ── Cadence & timing ─────────────────────────────────────────────────────────

const SPEED_OPTIONS: { value: SpeedTarget; label: string; sub: string }[] = [
  { value: "asap", label: "Instantly", sub: "Best shot at conversion" },
  { value: "five-min", label: "Within 5 min", sub: "Recommended" },
  {
    value: "fifteen-min",
    label: "Within 15 min",
    sub: "Still ahead of most teams",
  },
]

const PERSISTENCE_OPTIONS: {
  value: Persistence
  label: string
  sub: string
}[] = [
  { value: "light", label: "Light", sub: "A couple of tries over a day or two" }, // prettier-ignore
  { value: "standard", label: "Standard", sub: "A handful of tries across several days" }, // prettier-ignore
  { value: "persistent", label: "Persistent", sub: "More tries over about two weeks" }, // prettier-ignore
]

export function CadenceFields({
  data,
  onUpdate,
  showSpeed = true,
}: FieldsProps & { showSpeed?: boolean }) {
  return (
    <>
      {showSpeed && (
        <div className="space-y-3">
          <Label>Speed to first contact</Label>
          <OptionCards
            options={SPEED_OPTIONS}
            value={data.speed_target}
            onChange={(v) => onUpdate({ speed_target: v })}
          />
          <p className="text-muted-foreground text-xs">
            Outreach happens during local daytime hours. After-hours leads are
            contacted the next morning.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <Label>Persistence</Label>
        <OptionCards
          options={PERSISTENCE_OPTIONS}
          value={data.persistence}
          onChange={(v) => onUpdate({ persistence: v })}
        />
      </div>
    </>
  )
}

// ── Booking & handoff ────────────────────────────────────────────────────────

const CALENDAR_OPTIONS: { id: CalendarKind; label: string; logo: string }[] = [
  { id: "cal_com", label: "Cal.com", logo: calComIcon },
  { id: "calendly", label: "Calendly", logo: calendlyIcon },
]

export function CalendarFields({
  calendar,
  eventType,
  onCalendarChange,
  onEventTypeChange,
  calendars,
}: {
  calendar: string
  eventType: string
  onCalendarChange: (kind: string) => void
  onEventTypeChange: (uri: string) => void
  calendars: CalendarConnect
}) {
  const {
    connectedCalendars,
    calendlyEventTypes = [],
    calComEventTypes = [],
    returnTo,
  } = calendars
  const [calComDialogOpen, setCalComDialogOpen] = useState(false)

  // IMP-321: only treat a calendar as selected when it's actually connected, so
  // a stale persisted booking_calendar doesn't render as selected after the
  // integration is gone.
  const rawKind =
    calendar === "cal_com" || calendar === "calendly" ? calendar : ""
  const kind = rawKind && connectedCalendars[rawKind] ? rawKind : ""
  const connected = kind !== "" && connectedCalendars[kind]

  const eventTypes =
    kind === "calendly"
      ? calendlyEventTypes.map((et) => ({ value: et.uri, et }))
      : kind === "cal_com"
        ? calComEventTypes.map((et) => ({ value: String(et.id), et }))
        : []
  const hasEventTypes = eventTypes.length > 0

  function connectCalendar(calKind: CalendarKind) {
    // Cal.com uses a Personal Access Token — inline dialog, no redirect.
    // Calendly uses the per-user MCP OAuth flow (redirect out and back).
    if (calKind === "cal_com") {
      setCalComDialogOpen(true)
      return
    }
    window.location.assign(
      settingsMcpConnectPath(calKind) +
        `?return_to=${encodeURIComponent(returnTo)}`,
    )
  }

  return (
    <>
      <FormField label="Calendar integration">
        <div className="grid grid-cols-2 gap-3">
          {CALENDAR_OPTIONS.map(({ id, label, logo }) => {
            const isConnected = connectedCalendars[id]
            const selected = kind === id
            return (
              <div
                key={id}
                onClick={() => {
                  if (isConnected) onCalendarChange(id)
                }}
                className={cn(
                  "rounded-md border p-3 transition-colors",
                  selected
                    ? "border-blue-300 bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/60 dark:to-blue-900/20"
                    : "border-border",
                  isConnected && "cursor-pointer",
                  isConnected && !selected && "hover:border-blue-200",
                )}
              >
                <div className="mb-4 flex items-start justify-between">
                  <img src={logo} alt={label} className="size-7" />
                  {isConnected ? (
                    <span className="border-border inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium">
                      Connected
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        connectCalendar(id)
                      }}
                      className="border-border hover:bg-muted flex size-6 items-center justify-center rounded-sm border transition-colors"
                      aria-label={`Connect ${label}`}
                    >
                      <Plug className="text-muted-foreground size-3.5" />
                    </button>
                  )}
                </div>
                <p
                  className={cn(
                    "text-sm font-medium",
                    selected && "text-blue-600 dark:text-blue-400",
                  )}
                >
                  {label}
                </p>
              </div>
            )
          })}
        </div>
      </FormField>

      {/* A chosen event type is mandatory, picked from the provider's live list. */}
      {connected && hasEventTypes && (
        <FormField label="Event type">
          <select
            value={eventType}
            onChange={(e) => onEventTypeChange(e.target.value)}
            className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
          >
            <option value="">Choose an event type…</option>
            {eventTypes.map(({ value, et }) => (
              <option key={value} value={value}>
                {et.name}
                {et.duration_minutes ? ` (${et.duration_minutes} min)` : ""}
              </option>
            ))}
          </select>
        </FormField>
      )}

      {connected && !hasEventTypes && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>No bookable event types</AlertTitle>
          <AlertDescription>
            Create an event type in{" "}
            {kind === "calendly" ? "Calendly" : "cal.com"} first, then refresh —
            the agent books against a specific event type.
          </AlertDescription>
        </Alert>
      )}

      <CalComConnectDialog
        open={calComDialogOpen}
        onOpenChange={setCalComDialogOpen}
        returnTo={returnTo}
        onConnected={() => {
          // Inertia auto-reloads the integration props (connected_calendars +
          // cal_com_event_types); select cal.com so the user lands configured.
          onCalendarChange("cal_com")
          setCalComDialogOpen(false)
        }}
      />
    </>
  )
}

// IMP-409/176: connect Cal.com via a PAT without leaving the wizard/drawer.
// POSTs to the same credential-connect endpoint the settings page uses; on
// success Inertia reloads the integration props and the parent selects cal.com.
// Kept local (mirrors the receptionist step/panel copies) because each site
// wires a slightly different post-success behavior.
function CalComConnectDialog({
  open,
  onOpenChange,
  onConnected,
  returnTo,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConnected: () => void
  returnTo: string
}) {
  const [credentials, setCredentials] = useState<PatCredentials>({
    personalAccessToken: "",
  })
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const filled = credentials.personalAccessToken.trim() !== ""

  function reset() {
    setCredentials({ personalAccessToken: "" })
    setError(null)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setError(null)
    router.post(
      settingsIntegrationCredentialConnectPath("cal_com"),
      {
        personal_access_token: credentials.personalAccessToken,
        return_to: returnTo,
      },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          reset()
          onConnected()
        },
        onError: () => {
          setError(
            "Unable to verify token. Check that it's correct and try again.",
          )
        },
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset()
        onOpenChange(v)
      }}
    >
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Connect Cal.com</DialogTitle>
            <DialogDescription>
              Paste your Cal.com Personal Access Token. We&rsquo;ll use it to
              pull your event types and book appointments on your behalf.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertTitle>Connection failed</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <PatCredentialsForm
              values={credentials}
              onChange={setCredentials}
              idPrefix="lr-cal-com-cred"
              helpText="Generate this in Cal.com → Settings → Developer → API keys."
            />
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!filled || processing}
              loading={processing}
            >
              {processing ? "Connecting..." : "Connect"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function HandoffFields({
  data,
  onUpdate,
  calendars,
}: FieldsProps & { calendars: CalendarConnect }) {
  const callOn = data.channels.includes("voice")
  const transferOn = data.transfer_enabled && callOn
  const transferNumberInvalid =
    transferOn &&
    data.transfer_number.trim() !== "" &&
    !isValidUsPhoneNumber(data.transfer_number)

  // Capability order is fixed across every job: take messages, then book
  // appointments, then transfer calls. Lead follow-up has no message-taking
  // capability of its own, so it starts at appointments.
  return (
    <>
      <CapabilityCard
        icon={CalendarCheck}
        on={data.booking_enabled}
        title="Book appointments"
        description="Puts leads straight on your calendar."
        onToggle={(v) => onUpdate({ booking_enabled: v })}
      >
        <CalendarFields
          calendar={data.booking_calendar}
          eventType={data.booking_event_type}
          onCalendarChange={(v) => onUpdate({ booking_calendar: v })}
          onEventTypeChange={(v) => onUpdate({ booking_event_type: v })}
          calendars={calendars}
        />
      </CapabilityCard>

      <CapabilityCard
        icon={PhoneForwarded}
        on={transferOn}
        title="Transfer calls"
        description="Connects the lead to you live, during your hours."
        disabled={!callOn}
        disabledNote="Turn on phone calls to transfer the call."
        onToggle={(v) => onUpdate({ transfer_enabled: v })}
      >
        <FormField label="Transfer to">
          <Input
            type="tel"
            value={data.transfer_number}
            onChange={(e) =>
              onUpdate({ transfer_number: formatPhone(e.target.value) })
            }
            placeholder="e.g. (555) 000-0000"
            aria-invalid={transferNumberInvalid}
          />
          {transferNumberInvalid && (
            <p className="text-destructive mt-2 text-xs">
              Enter a valid US phone number.
            </p>
          )}
        </FormField>

        <FormField label="Available hours">
          {/* IMP-431: the shared weekly-hours grid (no 24h option, since the
                transfer-open backend has no all-day concept). */}
          <WeeklyHoursGrid
            hours={data.transfer_hours}
            allowAllDay={false}
            closedLabel="Unavailable"
            onHourChange={(idx, patch) =>
              onUpdate({
                transfer_hours: data.transfer_hours.map((row, i) =>
                  i === idx ? { ...row, ...patch } : row,
                ),
              })
            }
          />
        </FormField>
      </CapabilityCard>
    </>
  )
}
